import { Image as ImageIcon, Images, Heart, Star, Sparkle, Check } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { StatusDot } from "../StatusBadge";
import { MARKETPLACES } from "../../platforms";
import { fmtRelative, productImages } from "../../campaignUtils";

// Premium marketplace-style product card. Renders a field ONLY when the data really exists.
// Reused everywhere (Catalog, Wizard preview, Manual selection). When `selectable`
// is set it renders a checkbox and toggles via `onToggle` (single source, no duplicate card).
export function ProductCard({ product, testId, onClick, showMarketplace, selectable, selected, disabled, onToggle }) {
  const raw = product.raw || {};
  const images = productImages(product);
  const img = images[0];
  const imgCount = images.length;
  const category = raw.category;
  const favorers = typeof raw.num_favorers === "number" ? raw.num_favorers : null;
  const bestSeller = raw.best_seller === true || raw.is_bestseller === true;
  const isNew = raw.is_new === true || raw.new === true;
  const sku = raw.sku || product.external_id || product.id;
  const lastSync = product.updated_at || product.first_synced_at;
  const price = product.price;
  const mkt = MARKETPLACES.find((m) => m.key === product.platform);
  const handleClick = selectable ? () => { if (!disabled) onToggle?.(); } : onClick;
  const clickable = selectable ? !disabled : !!onClick;

  return (
    <div
      onClick={handleClick}
      className={cn(
        "group flex flex-col overflow-hidden rounded-lg border bg-card transition-colors",
        selected ? "border-brand ring-1 ring-brand" : "border-border hover:border-border/80 hover:bg-accent/20",
        clickable && "cursor-pointer",
        selectable && disabled && "cursor-not-allowed opacity-50",
      )}
      data-testid={testId}
      aria-pressed={selectable ? !!selected : undefined}
    >
      {/* Media */}
      <div className="relative aspect-square w-full overflow-hidden bg-accent/30">
        {selectable && (
          <span
            className={cn(
              "absolute right-2 top-2 z-10 flex h-5 w-5 items-center justify-center rounded border",
              selected ? "border-brand bg-brand text-brand-foreground" : "border-white/70 bg-background/70 backdrop-blur",
            )}
            data-testid={testId ? `${testId}-checkbox` : undefined}
          >
            {selected && <Check size={12} weight="bold" />}
          </span>
        )}
        {img ? (
          <img src={img} alt={product.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" loading="lazy" />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-muted-foreground/60">
            <ImageIcon size={26} weight="duotone" />
            <span className="text-[10px] uppercase tracking-wider">No image</span>
          </div>
        )}
        {/* Badges */}
        <div className="absolute left-2 top-2 flex flex-col gap-1">
          {bestSeller && (
            <span className="flex items-center gap-1 rounded-full bg-ai px-2 py-0.5 text-[10px] font-semibold text-ai-foreground shadow">
              <Star size={10} weight="fill" /> Best Seller
            </span>
          )}
          {isNew && (
            <span className="flex items-center gap-1 rounded-full bg-brand px-2 py-0.5 text-[10px] font-semibold text-brand-foreground shadow">
              <Sparkle size={10} weight="fill" /> New
            </span>
          )}
        </div>
        {showMarketplace && mkt && (
          <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-background/70 backdrop-blur" title={mkt.label}>
            <mkt.Icon size={12} />
          </span>
        )}
        {imgCount > 1 && (
          <span className="absolute bottom-2 right-2 flex items-center gap-1 rounded-full bg-background/70 px-2 py-0.5 text-[10px] text-foreground backdrop-blur">
            <Images size={11} /> {imgCount}
          </span>
        )}
      </div>


      {/* Body */}
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <div className="flex items-start justify-between gap-2">
          <p className="line-clamp-2 text-sm font-medium leading-snug">{product.title}</p>
          {favorers != null && (
            <span className="flex shrink-0 items-center gap-0.5 text-[11px] tabular-nums text-muted-foreground" title="favorers">
              <Heart size={11} weight="fill" /> {favorers}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {price != null && (
            <span className="text-sm font-semibold tabular-nums">{price} {product.currency || ""}</span>
          )}
          {category && (
            <span className="truncate rounded-full border border-border bg-accent/40 px-2 py-px text-[10px] text-muted-foreground">{category}</span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 border-t border-border/60 px-3 py-2 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          {product.status && <StatusDot status={product.status === "active" ? "connected" : product.status} />}
          {lastSync && <span>{fmtRelative(lastSync)}</span>}
        </span>
        {sku && <span className={cn("truncate font-mono")} title={sku}>{sku}</span>}
      </div>
    </div>
  );
}
